import { useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { LoadingSpinner } from "@/components/ui/loading-spinner"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { useGetInvitationByToken, useRespondToInvitation } from "@/hooks/api"
import { RespondCouncilAdminInvitationRequest } from "@/gen/api"
import { CheckCircle, XCircle, Mail, Calendar, User, Building } from "lucide-react"

const formatDate = (dateString?: string) => {
  if (!dateString) return "N/A"
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

const getStatusBadge = (status?: string, expired?: boolean) => {
  if (expired) return <Badge variant="destructive">Expired</Badge>
  if (!status) return <Badge variant="secondary">Unknown</Badge>

  switch (status.toLowerCase()) {
    case "pending":
      return <Badge variant="secondary">Pending</Badge>
    case "accepted":
      return <Badge variant="default">Accepted</Badge>
    case "rejected":
      return <Badge variant="destructive">Rejected</Badge>
    case "cancelled":
      return <Badge variant="outline">Cancelled</Badge>
    default:
      return <Badge variant="secondary">{status}</Badge>
  }
}

interface FormErrors {
  username?: string
  firstName?: string
  lastName?: string
  password?: string
  confirmPassword?: string
  message?: string
  submit?: string
}

export default function InvitationResponsePage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()

  const [action, setAction] = useState<"ACCEPT" | "REJECT" | null>(null)
  const [registrationData, setRegistrationData] = useState({
    username: "",
    firstName: "",
    lastName: "",
    password: "",
    confirmPassword: "",
    phoneNumber: "",
    message: "",
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    data: invitationResponse,
    isLoading,
    error,
  } = useGetInvitationByToken({
    token: token || "",
  })

  const invitation = invitationResponse?.data

  const respondToInvitation = useRespondToInvitation({
    onSuccess: () => {
      navigate("/login", {
        state: {
          message:
            action === "ACCEPT"
              ? "Invitation accepted successfully! Please log in with your credentials."
              : "Invitation declined. You may close this page.",
        },
      })
    },
    onError: (error) => {
      setErrors({ submit: error.message })
      setIsSubmitting(false)
    },
  })

  const validateForm = () => {
    const newErrors: FormErrors = {}

    if (action === "ACCEPT") {
      if (!registrationData.username.trim()) {
        newErrors.username = "Username is required"
      } else if (registrationData.username.length < 3) {
        newErrors.username = "Username must be at least 3 characters"
      }

      if (!registrationData.firstName.trim()) {
        newErrors.firstName = "First name is required"
      }

      if (!registrationData.lastName.trim()) {
        newErrors.lastName = "Last name is required"
      }

      if (!registrationData.password) {
        newErrors.password = "Password is required"
      } else if (registrationData.password.length < 6) {
        newErrors.password = "Password must be at least 6 characters"
      }

      if (registrationData.password !== registrationData.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match"
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = () => {
    if (!validateForm()) return

    setIsSubmitting(true)

    const request: RespondCouncilAdminInvitationRequest = {
      action: action!,
      message: registrationData.message || "",
      ...(action === "ACCEPT" && {
        registrationDetails: {
          username: registrationData.username,
          firstName: registrationData.firstName,
          lastName: registrationData.lastName,
          password: registrationData.password,
          ...(registrationData.phoneNumber ? { phoneNumber: registrationData.phoneNumber } : {}),
        },
      }),
    }

    respondToInvitation.mutate({
      token: token!,
      request,
    })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoadingSpinner className="size-8 mx-auto mb-4" />
          <p className="text-gray-600">Loading invitation...</p>
        </div>
      </div>
    )
  }

  if (error || !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <XCircle className="size-12 text-red-500 mx-auto mb-2" />
            <CardTitle>Invalid Invitation</CardTitle>
            <CardDescription>This invitation link is invalid or has expired.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/login")} className="w-full">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (invitation.status?.toLowerCase() !== "pending" || invitation.expired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <XCircle className="size-12 text-orange-500 mx-auto mb-2" />
            <CardTitle>Invitation No Longer Valid</CardTitle>
            <CardDescription>
              This invitation has already been {invitation.status?.toLowerCase()} or has expired.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-gray-600">
              <p>
                <strong>Status:</strong> {getStatusBadge(invitation.status, invitation.expired)}
              </p>
              <p>
                <strong>Invited:</strong> {formatDate(invitation.invitedAt)}
              </p>
              {invitation.expiresAt && (
                <p>
                  <strong>Expired:</strong> {formatDate(invitation.expiresAt)}
                </p>
              )}
            </div>
            <Button onClick={() => navigate("/login")} className="w-full">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-8">
      <div className="w-full max-w-2xl mx-auto px-4">
        <Card>
          <CardHeader className="text-center">
            <Mail className="size-12 text-blue-500 mx-auto mb-4" />
            <CardTitle className="text-2xl">Council Admin Invitation</CardTitle>
            <CardDescription>
              You've been invited to join {invitation.councilName} as an admin
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Invitation Details */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Building className="size-4 text-gray-500" />
                <span className="font-medium">Council:</span>
                <span>
                  {invitation.councilName} ({invitation.councilCode})
                </span>
              </div>
              <div className="flex items-center gap-2">
                <User className="size-4 text-gray-500" />
                <span className="font-medium">Invited by:</span>
                <span>{invitation.invitedByFullName}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="size-4 text-gray-500" />
                <span className="font-medium">Invited:</span>
                <span>{formatDate(invitation.invitedAt)}</span>
              </div>
              {invitation.expiresAt && (
                <div className="flex items-center gap-2">
                  <Calendar className="size-4 text-gray-500" />
                  <span className="font-medium">Expires:</span>
                  <span>{formatDate(invitation.expiresAt)}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="font-medium">Status:</span>
                {getStatusBadge(invitation.status, invitation.expired)}
              </div>
            </div>

            {/* Action Selection */}
            <div className="space-y-4">
              <Label className="text-base font-medium">What would you like to do?</Label>
              <div className="grid grid-cols-2 gap-4">
                <Button
                  variant={action === "ACCEPT" ? "default" : "outline"}
                  className="h-16 flex flex-col items-center gap-2"
                  onClick={() => {
                    setAction("ACCEPT")
                    setErrors({})
                  }}
                >
                  <CheckCircle className="size-5" />
                  Accept Invitation
                </Button>
                <Button
                  variant={action === "REJECT" ? "destructive" : "outline"}
                  className="h-16 flex flex-col items-center gap-2"
                  onClick={() => {
                    setAction("REJECT")
                    setErrors({})
                  }}
                >
                  <XCircle className="size-5" />
                  Decline Invitation
                </Button>
              </div>
            </div>

            {/* Registration Form for Accept */}
            {action === "ACCEPT" && (
              <div className="space-y-4 border rounded-lg p-4">
                <h3 className="font-medium">Create Your Account</h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input
                      id="firstName"
                      value={registrationData.firstName}
                      onChange={(e) => {
                        setRegistrationData((prev) => ({
                          ...prev,
                          firstName: e.target.value,
                        }))
                        if (errors.firstName) setErrors((prev) => ({ ...prev, firstName: "" }))
                      }}
                      placeholder="Enter your first name"
                    />
                    {errors.firstName && (
                      <p className="text-sm text-destructive">{errors.firstName}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input
                      id="lastName"
                      value={registrationData.lastName}
                      onChange={(e) => {
                        setRegistrationData((prev) => ({
                          ...prev,
                          lastName: e.target.value,
                        }))
                        if (errors.lastName) setErrors((prev) => ({ ...prev, lastName: "" }))
                      }}
                      placeholder="Enter your last name"
                    />
                    {errors.lastName && (
                      <p className="text-sm text-destructive">{errors.lastName}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="username">Username *</Label>
                  <Input
                    id="username"
                    value={registrationData.username}
                    onChange={(e) => {
                      setRegistrationData((prev) => ({
                        ...prev,
                        username: e.target.value,
                      }))
                      if (errors.username) setErrors((prev) => ({ ...prev, username: "" }))
                    }}
                    placeholder="Choose a username"
                  />
                  {errors.username && <p className="text-sm text-destructive">{errors.username}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={registrationData.password}
                    onChange={(e) => {
                      setRegistrationData((prev) => ({
                        ...prev,
                        password: e.target.value,
                      }))
                      if (errors.password) setErrors((prev) => ({ ...prev, password: "" }))
                    }}
                    placeholder="Create a password"
                  />
                  {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password *</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={registrationData.confirmPassword}
                    onChange={(e) => {
                      setRegistrationData((prev) => ({
                        ...prev,
                        confirmPassword: e.target.value,
                      }))
                      if (errors.confirmPassword)
                        setErrors((prev) => ({ ...prev, confirmPassword: "" }))
                    }}
                    placeholder="Confirm your password"
                  />
                  {errors.confirmPassword && (
                    <p className="text-sm text-destructive">{errors.confirmPassword}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phoneNumber">Phone Number (Optional)</Label>
                  <Input
                    id="phoneNumber"
                    value={registrationData.phoneNumber}
                    onChange={(e) =>
                      setRegistrationData((prev) => ({
                        ...prev,
                        phoneNumber: e.target.value,
                      }))
                    }
                    placeholder="Enter your phone number"
                  />
                </div>
              </div>
            )}

            {/* Decline Reason */}
            {action === "REJECT" && (
              <div className="space-y-2 border rounded-lg p-4">
                <Label htmlFor="declineReason">Reason for declining (Optional)</Label>
                <Input
                  id="declineReason"
                  value={registrationData.message}
                  onChange={(e) => {
                    setRegistrationData((prev) => ({
                      ...prev,
                      message: e.target.value,
                    }))
                    if (errors.message) setErrors((prev) => ({ ...prev, message: "" }))
                  }}
                  placeholder="Let us know why you're declining (optional)"
                />
                {errors.message && <p className="text-sm text-destructive">{errors.message}</p>}
              </div>
            )}

            {/* Error Display */}
            {errors.submit && (
              <Alert variant="destructive">
                <AlertDescription>{errors.submit}</AlertDescription>
              </Alert>
            )}

            {/* Submit Button */}
            {action && (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || respondToInvitation.isPending}
                className="w-full"
                variant={action === "REJECT" ? "destructive" : "default"}
              >
                {isSubmitting || respondToInvitation.isPending ? (
                  <LoadingSpinner className="size-4 mr-2" />
                ) : null}
                {action === "ACCEPT" ? "Accept & Create Account" : "Decline Invitation"}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
